import React from 'react';
import i18n from 'i18next';
import FormInput from './components/FormInput/FormInput';
import FormTextarea from './components/FormTextarea/FormTextarea';
import FormCheckbox from './components/FormCheckbox/FormCheckbox';
import FormSelect, { optionsProcessor } from './components/FormSelect/FormSelect';
import FormsRow from './components/FormsRow/FormsRow';

import { Namespace } from '../../../translation/namespace';
import { tryTranslate } from '../../../lib/translation/utils';
import { InputType } from './InputTypes';

const expandCssModifier = (modifier, standardModifier) => {
  if (modifier) {
    return `${modifier} ${standardModifier}`;
  }

  return standardModifier;
};

/**
 * Returns help texts for labels that are
 * - specially defined by their names
 * - of type 'email' or 'tel'
 * @param name
 * @param type
 * @return {null|*}
 */
export const getHelpText = (name, type) => {
  if (i18n.exists(`${Namespace.phrases}:labelHelpText.${name}`)) {
    return tryTranslate(`${Namespace.phrases}:labelHelpText.${name}`);
  }

  if (type === InputType.TEL || type === InputType.EMAIL) {
    return tryTranslate(`${Namespace.phrases}:validation.${type}`);
  }

  return null;
};

export const getPatternMismatchErrorMessage = (name, type) =>
  tryTranslate(`${Namespace.phrases}:patternMismatchErrorMessage.${name}`) ||
  tryTranslate(`${Namespace.phrases}:patternMismatchErrorMessage.${type}`);

const getInput = (parameter, idSuffix, onChange, labelTitle, labelHelpText, customSetOfAttributes) => (
  // parameter is never null or undefined here
  <FormsRow key={`${parameter.name}-${idSuffix}`}>
    <FormInput
      id={`${parameter.name}-${idSuffix}`}
      key={`${parameter.name}-${idSuffix}`}
      type={parameter.type}
      name={parameter.name}
      required={parameter.required}
      readOnly={parameter.readonly || parameter.constraints?.readonly}
      labelTitle={labelTitle}
      labelHelpText={labelHelpText}
      value={parameter.value}
      issues={parameter.getIssues()}
      minLength={parameter.constraints?.minLength}
      maxLength={parameter.constraints?.maxLength}
      // number constraints
      min={parameter.constraints?.min}
      max={parameter.constraints?.max}
      step={parameter.constraints?.step}
      // custom pattern mismatch errors only for types where the browser native message is not used.
      patternMismatchErrorMessage={getPatternMismatchErrorMessage(parameter.name, parameter.type)}
      onChange={event => onChange(parameter.name, event.target.value)}
      pattern={parameter.constraints?.pattern}
      {...customSetOfAttributes}
    />
  </FormsRow>
);

const getTextarea = (parameter, idSuffix, onChange, labelTitle, customSetOfAttributes) => (
  // parameter is never null or undefined here
  <FormsRow key={`${parameter.name}-${idSuffix}`}>
    <FormTextarea
      id={`textarea-${idSuffix}`}
      key={`${parameter.name}-${idSuffix}`}
      name={parameter.name}
      required={parameter.required}
      autoExpand
      labelTitle={labelTitle}
      value={parameter.value}
      readOnly={parameter.readonly || parameter.constraints?.readonly}
      maxLength={parameter.constraints?.maxLength || 1000}
      pattern={parameter.constraints?.pattern}
      onChange={({ target }) => onChange(target.name, target.value)}
      {...customSetOfAttributes}
      modifierTextarea={expandCssModifier(customSetOfAttributes.modifierTextarea, 'form-textarea--auto-expand')}
    />
  </FormsRow>
);

const getCheckbox = (parameter, idSuffix, onChange, labelTitle, customSetOfAttributes) => (
  // parameter is never null or undefined here
  <FormsRow key={`${parameter.name}-${idSuffix}`}>
    <FormCheckbox
      id={`checkbox-${idSuffix}`}
      key={`${parameter.name}-${idSuffix}`}
      name={parameter.name}
      required={parameter.required}
      labelTitle={labelTitle}
      checked={parameter.checked}
      value={parameter.value}
      onChange={parameter.onChange}
      {...customSetOfAttributes}
    />
  </FormsRow>
);

const getSelect = (parameter, idSuffix, onChange, labelTitle, customSetOfAttributes) => (
  // parameter is never null or undefined here
  <FormsRow key={`${parameter.name}-${idSuffix}`}>
    <FormSelect
      id={`select-${idSuffix}`}
      key={`${parameter.name}-${idSuffix}`}
      name={parameter.name}
      required={parameter.required}
      labelTitle={labelTitle}
      value={parameter.value}
      options={optionsProcessor(parameter.name, parameter.options)}
      onChange={event => onChange(event.target.name, event.target.value)}
      {...customSetOfAttributes}
    />
  </FormsRow>
);

export function createFormElement(customSetOfAttributes = {}) {
  return (parameter, idSuffix, onChange, labelTitle) => {
    if (!parameter) {
      return null;
    }

    switch (parameter.type) {
      case InputType.TEXT:
      case InputType.URL:
      case InputType.TEL:
      case InputType.EMAIL:
      case InputType.PASSWORD:
      case InputType.NUMBER:
        return getInput(
          parameter, // is never null or undefined here
          idSuffix,
          onChange,
          labelTitle,
          getHelpText(parameter.name, parameter.type),
          customSetOfAttributes,
        );
      case InputType.TEXTAREA:
        return getTextarea(parameter, idSuffix, onChange, labelTitle, customSetOfAttributes);
      case InputType.CHECKBOX:
        return getCheckbox(parameter, idSuffix, onChange, labelTitle, customSetOfAttributes);
      case InputType.SELECT:
        return getSelect(parameter, idSuffix, onChange, labelTitle, customSetOfAttributes);
      default:
        return null;
    }
  };
}
